import { VisitOutcome, VisitState } from '@/types/visit';
import moment from 'moment';

import { getVisitEventStatus } from './status';
import { CalendarEventClinician, CalendarTile, ServicesForCardDisplay } from './types';

// Appointment start/end, falling back to the effective date's day bounds when unset.
export const getAppointmentDateRange = (
    startDatetime: Date | string | null | undefined,
    endDatetime: Date | string | null | undefined,
    effectiveDate: Date | string,
): { start: Date; end: Date } => {
    const effectiveDateMoment = moment(effectiveDate);
    return {
        start: startDatetime
            ? startDatetime instanceof Date
                ? startDatetime
                : new Date(startDatetime)
            : effectiveDateMoment.startOf('day').toDate(),
        end: endDatetime
            ? endDatetime instanceof Date
                ? endDatetime
                : new Date(endDatetime)
            : effectiveDateMoment.endOf('day').toDate(),
    };
};

// Start and end of the given day.
export const getDayDateRange = (date: Date | string): { start: Date; end: Date } => {
    const dateMoment = moment(date);
    return {
        start: dateMoment.startOf('day').toDate(),
        end: dateMoment.endOf('day').toDate(),
    };
};

// Normalized input both calendars map into before calling createVisitEvent (one per appointment).
export type CalendarVisitInput = {
    id: string;
    date: Date;
    timezone: string;
    state: VisitState;
    outcome: VisitOutcome;
    disciplines: { id: string; code: string }[];
    appointment: {
        id: string;
        startDatetime: Date | null;
        endDatetime: Date | null;
        clinician: CalendarEventClinician | null;
        services: ServicesForCardDisplay[];
    };
};

// Build a calendar tile from a normalized visit + appointment.
export const createVisitEvent = (input: CalendarVisitInput): CalendarTile => {
    const { appointment } = input;
    const { start, end } = getAppointmentDateRange(
        appointment.startDatetime,
        appointment.endDatetime,
        input.date,
    );

    const status = getVisitEventStatus(input.state, input.outcome, {
        services: appointment.services,
        clinician: appointment.clinician,
        startDatetime: appointment.startDatetime,
        endDatetime: appointment.endDatetime,
    });

    return {
        kind: 'tile',
        __typename: 'Visit',
        id: input.id,
        effectiveDate: input.date,
        state: input.state,
        outcome: input.outcome,
        timezone: input.timezone,
        disciplines: input.disciplines,
        start,
        end,
        title: 'Visit',
        uuid: `${input.id}-${appointment.id}`,
        status,
        appointment: {
            id: appointment.id,
            startDatetime: appointment.startDatetime,
            endDatetime: appointment.endDatetime,
            clinician: appointment.clinician,
            services: appointment.services,
        },
    };
};
